/**
 * Small morph motions in the same style as the Notify me bar:
 * - Cart count: when the number in the header bag changes, the old number blurs
 *   up and away and the new one rolls in from below; the first item pops the badge in.
 * Transform, opacity and a small blur only. Reduced motion: none.
 */
const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const EASE = 'cubic-bezier(0.32, 1.3, 0.4, 1)';

let lastCount = null;
const readCount = () => {
  const badge = document.querySelector('[data-cart-badge]');
  return { badge, count: badge ? parseInt(badge.textContent.trim(), 10) || 0 : 0 };
};

const animateBadge = (badge, from, to) => {
  if (reduce() || !badge) return;
  if (from === 0) {
    badge.animate([{ scale: '0.2', opacity: 0 }, { scale: '1', opacity: 1 }], { duration: 520, easing: EASE });
    return;
  }
  const up = to > from;
  const old = badge.cloneNode(true);
  old.removeAttribute('data-cart-badge');
  old.setAttribute('aria-hidden', 'true');
  old.style.pointerEvents = 'none';
  badge.after(old);
  old.animate(
    [{ opacity: 1, filter: 'blur(0px)', transform: 'translateY(0)' }, { opacity: 0, filter: 'blur(4px)', transform: `translateY(${up ? '-60%' : '60%'})` }],
    { duration: 300, easing: 'cubic-bezier(0.4, 0, 0.7, 0.2)', fill: 'forwards' }
  ).finished.then(() => old.remove(), () => old.remove());
  badge.animate(
    [{ opacity: 0, filter: 'blur(4px)', transform: `translateY(${up ? '60%' : '-60%'})` }, { opacity: 1, filter: 'blur(0px)', transform: 'translateY(0)' }],
    { duration: 480, delay: 60, easing: EASE, fill: 'backwards' }
  );
};

const check = () => {
  const { badge, count } = readCount();
  if (lastCount !== null && count !== lastCount && count > 0) animateBadge(badge, lastCount, count);
  lastCount = count;
};

lastCount = readCount().count;
const header = document.querySelector('header') || document.body;
new MutationObserver(check).observe(header, { childList: true, subtree: true, characterData: true });

/*
 * Search, cart and menu: each pop-up grows out of the button that opened it
 * (starting clipped to the button's round shape), its contents blur into focus,
 * and on close it folds back into that button. clip-path, opacity, small blur only.
 * The cart also folds back into the header bag when it opened on its own (after Add to cart).
 */
const MORPH_PANELS = {
  'predictive-search': { fallback: null },
  'cart-drawer': { fallback: '#header-cart-trigger' },
  'mobile-nav-drawer': { fallback: null },
};
const lastTrigger = new Map();
const visible = (el) => !!el && el.isConnected && el.getBoundingClientRect().width > 0;
const panelRadius = (dialog) => parseFloat(getComputedStyle(dialog).borderTopLeftRadius) || 28;

const clipToTrigger = (dialog, trigger) => {
  const from = trigger.getBoundingClientRect();
  const box = dialog.getBoundingClientRect();
  const top = Math.max(0, from.top - box.top);
  const left = Math.max(0, from.left - box.left);
  const right = Math.max(0, box.right - from.right);
  const bottom = Math.max(0, box.bottom - from.bottom);
  return `inset(${top}px ${right}px ${bottom}px ${left}px round ${Math.min(from.height / 2, 28)}px)`;
};

document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[commandfor]') : null;
  const id = trigger?.getAttribute('commandfor');
  if (!id || !(id in MORPH_PANELS)) return;
  const dialog = document.getElementById(id);
  if (!dialog || dialog.open || !visible(trigger)) return;
  if (id === 'predictive-search') {
    // Phones: the search pop-up opens just under the button that was tapped (see bubble-styles).
    dialog.style.setProperty('--search-top', `${Math.round(trigger.getBoundingClientRect().bottom + 8)}px`);
  }
  lastTrigger.set(id, trigger);
  if (reduce()) return;
  dialog.classList.add('is-morph-open');
  requestAnimationFrame(() => {
    if (!dialog.open) { dialog.classList.remove('is-morph-open'); return; }
    const radius = `${panelRadius(dialog)}px`;
    const grow = dialog.animate(
      [{ clipPath: clipToTrigger(dialog, trigger), opacity: 0.6 }, { clipPath: `inset(0px 0px 0px 0px round ${radius})`, opacity: 1 }],
      { duration: 560, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
    );
    [...dialog.children].forEach((child, i) => child.animate(
      [{ opacity: 0, filter: 'blur(8px)', transform: 'translateY(-6px)' }, { opacity: 1, filter: 'blur(0px)', transform: 'none' }],
      { duration: 380, delay: 140 + i * 50, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)', fill: 'backwards' }
    ));
    grow.finished.finally(() => dialog.classList.remove('is-morph-open'));
  });
}, true);

const watchClose = (id) => {
  const dialog = document.getElementById(id);
  if (!dialog) return;
  new MutationObserver(() => {
    if (!dialog.hasAttribute('data-closing') || dialog.classList.contains('is-morph-close') || reduce()) return;
    let trigger = lastTrigger.get(id);
    if (!visible(trigger)) trigger = MORPH_PANELS[id].fallback ? document.querySelector(MORPH_PANELS[id].fallback) : null;
    if (!visible(trigger)) return; // no button to fold into: the regular close plays
    const ms = parseFloat(getComputedStyle(dialog).getPropertyValue('--close-duration')) || 400;
    const radius = `${panelRadius(dialog)}px`;
    dialog.classList.add('is-morph-close');
    [...dialog.children].forEach((child) => child.animate(
      [{ opacity: 1, filter: 'blur(0px)', transform: 'none' }, { opacity: 0, filter: 'blur(6px)', transform: 'translateY(-4px)' }],
      { duration: ms * 0.5, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
    ));
    const target = clipToTrigger(dialog, trigger);
    dialog.animate(
      [
        { clipPath: `inset(0px 0px 0px 0px round ${radius})`, opacity: 1 },
        { clipPath: target, opacity: 0.85, offset: 0.85 },
        { clipPath: target, opacity: 0 },
      ],
      { duration: ms, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'forwards' }
    );
    // s-dialog closes the dialog when the time is up (or cancels the close); tidy up right after.
    const done = new MutationObserver(() => {
      if (dialog.open && dialog.hasAttribute('data-closing')) return;
      done.disconnect();
      dialog.classList.remove('is-morph-close');
      dialog.getAnimations({ subtree: true }).forEach((a) => a.cancel());
    });
    done.observe(dialog, { attributes: true, attributeFilter: ['open', 'data-closing'] });
  }).observe(dialog, { attributes: true, attributeFilter: ['data-closing'] });
};
Object.keys(MORPH_PANELS).forEach(watchClose);
